import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutDashboard, Users, BarChart3, Download, Settings, ScanLine } from "lucide-react";
import { DoItSignature } from "@/components/brand/Brand";
import { CandidatesTable } from "@/components/portal/CandidatesTable";
import { CandidateSheet } from "@/components/portal/CandidateSheet";
import { PortalResumen, PortalStats, PortalExport, PortalCuenta } from "@/components/portal/PortalSections";
import { CvDialog } from "@/components/cv/CvDialog";
import { useDemo } from "@/lib/demo/store";
import { companyStats } from "@/lib/demo/analytics";
import { byRecent } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";
import { LangSwitcher } from "@/components/i18n/LangSwitcher";

const SECTIONS = [
  { id: "resumen", label: "Resumen", icon: LayoutDashboard },
  { id: "candidatos", label: "Candidatos", icon: Users },
  { id: "estadisticas", label: "Estadísticas", icon: BarChart3 },
  { id: "exportar", label: "Exportar", icon: Download },
  { id: "cuenta", label: "Cuenta", icon: Settings },
];

export default function EmpresaPortal() {
  const [params, setParams] = useSearchParams();
  const section = SECTIONS.find((s) => s.id === params.get("s"))?.id || "resumen";
  const { state, participantsById } = useDemo();
  const { t } = useI18n();
  const stats = useMemo(() => companyStats(state, "bosch"), [state]);
  const rows = useMemo(() => state.interactions
    .filter((i) => i.companyId === "bosch")
    .sort(byRecent)
    .map((interaction) => ({ interaction, participant: participantsById[interaction.participantId] }))
    .filter((r) => r.participant), [state, participantsById]);
  const [selected, setSelected] = useState(null);
  const [cvFor, setCvFor] = useState(null);
  const go = (id) => setParams({ s: id });
  const current = SECTIONS.find((s) => s.id === section);

  return (
    <div className="min-h-screen bg-[#F6F7F9] pb-28 lg:flex lg:pb-0" data-testid="company-portal-page">
      <aside className="border-b border-slate-200 bg-white lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col lg:border-b-0 lg:border-r">
        <div className="px-6 pb-4 pt-6 lg:pb-8 lg:pt-8">
          <p className="font-display text-3xl font-extrabold tracking-tight" data-testid="portal-company-name">BOSCH</p>
          <p className="text-xs text-slate-500">{t("Foro de Empleo UPV 2027")}</p>
        </div>
        <nav className="no-scrollbar flex gap-1 overflow-x-auto px-4 pb-3 lg:flex-col lg:pb-0">
          {SECTIONS.map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => go(id)} className={`flex shrink-0 items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${section === id ? "bg-slate-950 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`portal-nav-${id}`}>
              <Icon className="h-4 w-4" /> {t(label)}
            </button>
          ))}
        </nav>
        <div className="mt-auto hidden space-y-4 p-6 lg:block">
          <Link to="/empresa/scanner" className="flex items-center gap-2 rounded-xl bg-upv px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-upv-dark" data-testid="portal-scanner-link"><ScanLine className="h-4 w-4" /> {t("Abrir scanner")}</Link>
          <DoItSignature />
        </div>
      </aside>
      <main className="min-w-0 flex-1 px-5 py-8 lg:px-10 lg:py-10">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Portal empresa")}</p>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight" data-testid="portal-section-title">{t(current.label)}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
          <LangSwitcher testPrefix="portal-lang" />
          <span className="flex w-fit items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700" data-testid="portal-sync-badge">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> {t("Sincronizado en directo con 3 dispositivos")}
          </span>
          </div>
        </div>
        {section === "resumen" && <PortalResumen stats={stats} rows={rows} onOpen={setSelected} />}
        {section === "candidatos" && <CandidatesTable rows={rows} total={stats.total} onOpen={setSelected} onViewCv={setCvFor} />}
        {section === "estadisticas" && <PortalStats stats={stats} />}
        {section === "exportar" && <PortalExport rows={rows} total={stats.total} />}
        {section === "cuenta" && <PortalCuenta stats={stats} />}
      </main>
      <CandidateSheet row={selected} onClose={() => setSelected(null)} onViewCv={setCvFor} />
      <CvDialog participant={cvFor} onClose={() => setCvFor(null)} />
    </div>
  );
}
